import React from 'react';
import moment from 'moment-timezone';
import { FormattedMessage } from 'react-intl';
import { CsvButton } from '../../../components/richButtons';
import { isSameWeek, getLastPeriodEnd, fullDateYear } from '../../../utilities/dateTime';

const round2places = n => Math.round(n * 100) / 100;
const getHours = hoursData => (hoursData && hoursData.minutes ? round2places(hoursData.minutes / 60) : 0);

function summarizePayWeekSingleRate(payWeek) {
    const result = {
        'Regular Hours': 0.0,
        'Regular Rate': 0.0,
        'Overtime Hours': 0.0,
        'Overtime Rate': 0.0,
        'Doubletime Hours': 0.0,
        'Doubletime Rate': 0.0
    };

    for (const payItem of payWeek) {
        if (payItem.multiplier === 1) {
            result['Regular Hours'] += getHours(payItem);
            result['Regular Rate'] = payItem.rate * payItem.multiplier;
        } else if (payItem.multiplier === 2) {
            result['Doubletime Hours'] += getHours(payItem);
            result['Doubletime Rate'] = payItem.rate * payItem.multiplier;
        } else if (payItem.multiplier > 1 && payItem.multiplier < 2) {
            result['Overtime Hours'] += getHours(payItem);
            result['Overtime Rate'] = payItem.rate * payItem.multiplier;
        }
    }

    return result;
}

function summarizePayWeek(payWeek) {
    if (!payWeek || payWeek.length === 0) return [];

    const rates = payWeek.map(item => item.rate);
    const summary = [];

    for (const rate of [...new Set(rates)]) {
        const payWeekOneRate = payWeek.filter(item => item.rate === rate);
        summary.push(summarizePayWeekSingleRate(payWeekOneRate));
    }
    return summary;
}

export function exportPayroll(timesheets, clients, onlyLastWeek = true) {
    if (!timesheets) {
        return <div>No timesheets to export.</div>;
    }

    const list = onlyLastWeek
        ? timesheets.filter(t => isSameWeek(t.period_ending, moment.tz(t.job.work_timezone).add(-7, 'days'), t.job.work_timezone))
        : timesheets;

    if (!list || list.length === 0) {
        return <div>No timesheets to export for most recent week.</div>;
    }

    // TODO - multiple rates for same worker
    // this works only when the worker has a single rate
    const arrayPerWorker = list.map(timesheet => {
        const { job, period_ending, paysheet } = timesheet;
        const client = clients.find(c => c.id === job.client_id);
        const { payWeek } = paysheet || {};

        const rowHeader = {
            'Client Name': client ? client.name : '',
            'Worker Name': job.contractor ? job.contractor.name : '',
            'Period Ending': fullDateYear(moment.tz(period_ending, job.work_timezone))
        };

        return summarizePayWeek(payWeek).map(payRow => ({ ...rowHeader, ...payRow }));
    });

    const arrayFlat = arrayPerWorker.flat();

    const periodEnd = getLastPeriodEnd().format('YYYY-MM-DD');
    const fileName = `PayrollExport-${periodEnd}`;
    return (
        <CsvButton
            data={arrayFlat}
            filename={fileName}
            text={<FormattedMessage id={'exportPayrollCsv'} defaultMessage={`Export Payroll ${periodEnd} (CSV)`} />}
        />
    );
}

export function exportExecupayPayroll(timesheets, admin, onlyLastWeek = true) {
    if (!timesheets) {
        return <div>No timesheets to export.</div>;
    }

    const list = onlyLastWeek
        ? timesheets.filter(t => isSameWeek(t.period_ending, moment.tz(t.job.work_timezone).add(-7, 'days'), t.job.work_timezone))
        : timesheets;

    if (!list || list.length === 0) {
        return <div>No timesheets to export for most recent week.</div>;
    }

    const arrayPerWorker = list.map(timesheet => {
        const { job, period_ending, paysheet } = timesheet;
        const { payWeek } = paysheet || {};
        const { execupay_client_code } = admin;
        const { user } = job || {};
        const { execupay_employeeid } = user || {};

        const rowHeader = {
            'Client Code': execupay_client_code || '',
            'Employee Number': execupay_employeeid || '',
            Division: '',
            Department: '',
            Type: 'P',
            Code: 0,
            'Pay Rate': job.rate,
            'Rate #': '',
            'Regular Hours': 0,
            'Overtime Hours': 0,
            Amount: '',
            'Job 1': '',
            'Job 2': '',
            'Job 3': '',
            'Job 4': '',
            'Job 5': '',
            Date: '',
            'Week Number': moment.tz(period_ending, job.work_timezone).week()
        };

        const payRows = summarizePayWeek(payWeek).map(payRow => ({
            'Pay Rate': payRow['Regular Rate'],
            'Regular Hours': payRow['Regular Hours'],
            'Overtime Hours': payRow['Overtime Hours']
        }));

        return payRows.map(payRow => ({ ...rowHeader, ...payRow }));
    });

    const arrayFlat = arrayPerWorker.flat();

    const periodEnd = getLastPeriodEnd().format('YYYY-MM-DD');
    const fileName = `ExecupayExport-${periodEnd}`;
    return (
        <CsvButton
            data={arrayFlat}
            filename={fileName}
            text={<FormattedMessage id={'exportExecupayPayrollCsv'} defaultMessage={`Export Payroll ${periodEnd} (Execupay)`} />}
        />
    );
}

export function calculateTotal(timesheet) {
    if (!timesheet) return 0;

    const { job, paySheet } = timesheet;
    if (!job || !paySheet) return 0;

    const { rate } = job;
    const { weekPayItems } = paySheet;
    if (!weekPayItems) return 0;

    const { doubleTimeHours, overtimeHours, regularTimeHours } = weekPayItems;
    let sum = doubleTimeHours ? doubleTimeHours.multiplier * rate * doubleTimeHours.minutes / 60 : 0;
    sum += overtimeHours ? overtimeHours.multiplier * rate * overtimeHours.minutes / 60 : sum;
    sum += regularTimeHours ? regularTimeHours.multiplier * rate * regularTimeHours.minutes / 60 : sum;

    return sum;
}



// WEBPACK FOOTER //
// ./src/pages/timekeeping/exports/payroll.jsx